// Search bar: keyword + location (zip or "use my location"), radius control,
// and a category multi-select. Resolves location, then triggers a search.

import { geocodeZip, ApiError } from '../lib/api';
import { CATEGORIES } from '../lib/categories';
import { RADIUS_OPTIONS } from '../lib/constants';
import { loadPrefs, savePrefs } from '../lib/prefs';
import { store } from '../lib/state';

export interface SearchBarHandlers {
  // Runs the actual /api/events fetch using the current store location/keyword.
  runSearch: () => Promise<void>;
}

export function createSearchBar(root: HTMLElement, handlers: SearchBarHandlers): void {
  root.innerHTML = `
    <form class="search" id="search-form" autocomplete="off">
      <div class="search-row">
        <label class="field grow">
          <span class="field-label">Search</span>
          <input id="kw" type="search" placeholder="concerts, art, markets, a band name..."
                 aria-label="Keyword" />
        </label>
        <label class="field">
          <span class="field-label">Zip code</span>
          <input id="zip" inputmode="numeric" pattern="\\d{5}" maxlength="5"
                 placeholder="80202" aria-label="Zip code" />
        </label>
        <button type="button" id="geo-btn" class="btn ghost" title="Use my current location">
          📍 Near me
        </button>
        <label class="field">
          <span class="field-label">Radius</span>
          <select id="radius" aria-label="Search radius in miles">
            ${RADIUS_OPTIONS.map(
              (r) => `<option value="${r}" ${r === 25 ? 'selected' : ''}>${r} mi</option>`
            ).join('')}
          </select>
        </label>
        <button type="submit" id="search-btn" class="btn primary">Search</button>
      </div>
      <div class="search-row categories" id="cats" role="group" aria-label="Filter by category">
        <button type="button" class="chip chip-all" data-cat="all"
                style="--chip:#c9a44d" aria-pressed="true">
          <span aria-hidden="true">✦</span> All
        </button>
        ${CATEGORIES.map(
          (c) => `
          <button type="button" class="chip" data-cat="${c.id}"
                  style="--chip:${c.color}" aria-pressed="false">
            <span aria-hidden="true">${c.emoji}</span> ${c.label}
          </button>`
        ).join('')}
      </div>
      <p class="search-status" id="loc-status" role="status" aria-live="polite"></p>
    </form>
  `;

  const form = root.querySelector<HTMLFormElement>('#search-form')!;
  const kw = root.querySelector<HTMLInputElement>('#kw')!;
  const zip = root.querySelector<HTMLInputElement>('#zip')!;
  const radius = root.querySelector<HTMLSelectElement>('#radius')!;
  const geoBtn = root.querySelector<HTMLButtonElement>('#geo-btn')!;
  const locStatus = root.querySelector<HTMLParagraphElement>('#loc-status')!;
  const catsWrap = root.querySelector<HTMLDivElement>('#cats')!;

  function setStatus(msg: string, kind: 'info' | 'error' = 'info'): void {
    locStatus.textContent = msg;
    locStatus.dataset.kind = kind;
  }

  // Persist the current search whenever the store settles so a refresh or
  // return visit restores it. Reads the zip from the input (not in the store).
  function persist(): void {
    const s = store.get();
    if (!s.location) return;
    savePrefs({
      keyword: s.keyword,
      zip: zip.value.trim(),
      radiusMiles: s.radiusMiles,
      location: s.location,
      categories: [...s.categoryFilter],
      sortBy: s.sortBy,
    });
  }
  store.subscribe(persist);

  // Category chips filter the already-loaded results instantly, with no network
  // round-trip: the store update re-runs the client-side visibleEvents filter.
  catsWrap.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.chip');
    if (!btn) return;
    const cat = btn.dataset.cat;
    // "All" clears every category filter (show everything); a specific chip
    // toggles that one.
    if (cat === 'all') store.clearCategoryFilter();
    else store.toggleCategory(cat as (typeof CATEGORIES)[number]['id']);
  });

  // Keep chip pressed-state in sync with the store. "All" is active when no
  // specific category is selected.
  store.subscribe((state) => {
    catsWrap.querySelectorAll<HTMLButtonElement>('.chip').forEach((chip) => {
      const cat = chip.dataset.cat;
      const on = cat === 'all' ? state.categoryFilter.size === 0 : state.categoryFilter.has(cat as never);
      chip.setAttribute('aria-pressed', String(on));
      chip.classList.toggle('on', on);
    });
  });

  async function resolveZipAndSearch(): Promise<void> {
    const z = zip.value.trim();
    if (!/^\d{5}$/.test(z)) {
      setStatus('Enter a 5-digit US zip code, or use "Near me".', 'error');
      zip.focus();
      return;
    }
    setStatus('Looking up zip code...');
    try {
      const geo = await geocodeZip(z);
      const label = [geo.city, geo.state].filter(Boolean).join(', ') + ` ${z}`;
      store.set({
        location: { lat: geo.lat, lng: geo.lng, label: label.trim() },
        keyword: kw.value.trim(),
        radiusMiles: Number(radius.value),
      });
      setStatus(`Showing events near ${label.trim()}.`);
      await handlers.runSearch();
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : 'Could not look up that zip code.';
      setStatus(msg, 'error');
    }
  }

  function useMyLocation(): void {
    if (!('geolocation' in navigator)) {
      setStatus('Geolocation is not available in this browser. Try a zip code.', 'error');
      return;
    }
    setStatus('Requesting your location...');
    geoBtn.disabled = true;

    const onOk = async (pos: GeolocationPosition): Promise<void> => {
      geoBtn.disabled = false;
      // Searching by device location: clear any stale zip so the field does not
      // misrepresent where results are coming from.
      zip.value = '';
      store.set({
        location: {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          label: 'Current location',
        },
        keyword: kw.value.trim(),
        radiusMiles: Number(radius.value),
      });
      setStatus('Showing events near your current location.');
      await handlers.runSearch();
    };

    const onErr = (err: GeolocationPositionError, triedHighAccuracy: boolean): void => {
      // A network-location timeout is common on Android (no Google location
      // services / Wi-Fi off). Retry once with GPS before giving up.
      if (err.code === err.TIMEOUT && !triedHighAccuracy) {
        setStatus('Still locating (using GPS)...');
        navigator.geolocation.getCurrentPosition(
          onOk,
          (e) => onErr(e, true),
          { enableHighAccuracy: true, timeout: 20000, maximumAge: 600000 }
        );
        return;
      }
      geoBtn.disabled = false;
      const msg =
        err.code === err.PERMISSION_DENIED
          ? 'Location is blocked for this site. Allow it in your browser settings (tap the lock icon in the address bar), or enter a zip code.'
          : err.code === err.POSITION_UNAVAILABLE
            ? 'Your location is unavailable. Make sure device location is turned on, or enter a zip code.'
            : 'Locating timed out. Tap "Near me" to try again, or enter a zip code.';
      setStatus(msg, 'error');
    };

    navigator.geolocation.getCurrentPosition(
      onOk,
      (e) => onErr(e, false),
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 600000 }
    );
  }

  geoBtn.addEventListener('click', useMyLocation);

  // Radius is store-driven so it stays in sync with the calendar's radius
  // control. Changing it re-runs the search when a location is already set.
  radius.addEventListener('change', () => {
    store.set({ radiusMiles: Number(radius.value) });
    if (store.get().location) void handlers.runSearch();
  });
  store.subscribe((state) => {
    if (Number(radius.value) !== state.radiusMiles) radius.value = String(state.radiusMiles);
  });

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const state = store.get();
    // If we already have a location and the zip field is empty, just re-search
    // (e.g. the user only changed the keyword or radius).
    if (!zip.value.trim() && state.location) {
      store.set({ keyword: kw.value.trim(), radiusMiles: Number(radius.value) });
      void handlers.runSearch();
      return;
    }
    void resolveZipAndSearch();
  });

  // On first load, restore the last search if we have one. Otherwise decide how
  // to locate based on the geolocation permission state. Auto-prompting on load
  // (no user gesture) is unreliable on mobile, so we only auto-locate when
  // permission was already granted; otherwise we invite a tap on "Near me".
  async function restoreOrLocate(): Promise<void> {
    const prefs = loadPrefs();
    if (prefs && prefs.location) {
      kw.value = prefs.keyword;
      zip.value = prefs.zip;
      radius.value = String(prefs.radiusMiles);
      store.set({
        location: prefs.location,
        keyword: prefs.keyword,
        radiusMiles: prefs.radiusMiles,
        categoryFilter: new Set(prefs.categories),
        sortBy: prefs.sortBy,
      });
      setStatus(`Showing events near ${prefs.location.label}.`);
      void handlers.runSearch();
      return;
    }

    if (!('geolocation' in navigator)) {
      setStatus('Enter a zip code to find events near you.');
      return;
    }

    let state: PermissionState | undefined;
    try {
      const status = await navigator.permissions?.query({ name: 'geolocation' as PermissionName });
      state = status?.state;
    } catch {
      // Permissions API unsupported (older Android WebView): fall through.
    }

    if (state === 'granted') {
      setStatus('Finding events near you...');
      useMyLocation();
    } else if (state === 'denied') {
      setStatus('Location is blocked. Allow it in your browser settings, or enter a zip code below.');
    } else {
      // 'prompt' or unknown: a tap is the reliable way to get the permission
      // dialog on mobile, so ask for one instead of firing it automatically.
      setStatus('Tap "Near me" to use your location, or enter a zip code.');
    }
  }

  if (!store.get().location) void restoreOrLocate();
}
